import React, { useEffect, useState, useRef } from "react";

interface CustomAutocompleteOption {
  value: string | number;
  label: string;
  email?: string;
}

interface CustomAutocompleteProps {
  options: CustomAutocompleteOption[];
  value?: string | number;
  onChange: (value: any) => void;
  placeholder?: string;
  filterOption?: (option: CustomAutocompleteOption, inputValue: string) => boolean;
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  options,
  value,
  onChange,
  placeholder,
  filterOption,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Using loose equality to handle string/number ID mismatches
    const selectedOption = options.find((option) => option.value == value);
    setInputValue(selectedOption ? selectedOption.label : "");
  }, [value, options]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    setIsOpen(true);
  };

  const handleOptionSelect = (option: {
    value: string | number;
    label: string;
  }) => {
    onChange(option.value);
    setInputValue(option.label);
    setIsOpen(false);
  };

  const filteredOptions = options.filter((option) => {
    if (filterOption) return filterOption(option, inputValue);
    const val = inputValue.toLowerCase();
    return (
      (option.label && option.label.toLowerCase().includes(val)) ||
      (option.email && option.email.toLowerCase().includes(val))
    );
  });

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative group">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full h-14 px-4 bg-gray-50 border border-gray-100 focus:bg-white focus:border-primary/20 rounded-2xl font-medium text-text outline-none transition-all group-hover:border-gray-200"
        />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-text/20 group-hover:text-primary/40 transition-colors">
           <svg className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
           </svg>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-[100] mt-3 w-full bg-white/95 backdrop-blur-xl border border-gray-50 rounded-[2rem] shadow-2xl shadow-black/10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 max-h-80 no-scrollbar overflow-y-auto">
          {filteredOptions.length > 0 ? (
            <div className="p-2 space-y-1">
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionSelect(option)}
                  className={`flex flex-col px-5 py-4 cursor-pointer rounded-2xl transition-all ${value == option.value ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'hover:bg-gray-50 text-title'}`}
                >
                  <span className={`text-[13px] font-black uppercase tracking-tight ${value == option.value ? 'text-white' : 'text-title'}`}>
                    {option.label}
                  </span>
                  {option.email && (
                    <span className={`text-[10px] font-medium lowercase ${value === option.value ? 'text-white/70' : 'text-text/40'}`}>
                      {option.email}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center">
               <p className="text-[10px] font-black text-text/20 uppercase tracking-[0.3em]">Aucun résultat trouvé</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomAutocomplete;
